import { Expander } from '@carvana/helios-layout';
import { Button } from '@carvana/helios-navigation';
import { useTestFunc } from '@carvana/test-attributes';
import useAnalyticEvent from '../../shared/hooks/useAnalyticEvent';

import {
  FAQAccordionWrapper,
  FAQButtonWrapper,
  FAQHeader,
  FAQSectionWrapper,
  SubText,
  TextContainer
} from './FAQSection.styles';

const FAQData = () => [
  {
    q: 'How do I sell my car online with Carvana?',
    a: [
      'The process is simple and 100% online. To get a real offer for your car or truck, we will ask a few questions, starting with your license plate number or vehicle identification number (VIN), which helps us gather specific information about your vehicle.  We’ll ask a few quick questions about your vehicle’s overall condition, mileage, and features. Based on this information, we’re able to make you a real offer, instantly.',
      'From there, we will ask you to upload a few documents to verify you can sell the car, and then you choose how you’d like to be paid, and schedule an appointment to complete the sale. We have tons of Carvana locations, making it easy to drop off your vehicle. We also offer pickup, for an additional service charge, for customers in specific locations.'
    ]
  },
  {
    q: 'Can I sell my vehicle to Carvana without buying a car from Carvana?',
    a: [
      'Yes! You can absolutely sell us your car, even if you’re not ready to buy another car now. It’s an easy, online process to sell or trade in your vehicle. We buy thousands of cars each week from customers like you.'
    ]
  },
  {
    q: 'How do I trade in a vehicle with Carvana?',
    a: [
      'A “trade in” happens when you sell your current vehicle as part of the purchase of your next vehicle. Trading in your vehicle with Carvana is simple and can increase your buying power by lowering your down payment. Plus, in many states there are big sales tax savings only available when you trade in.',
      'To get started, answer a few questions to get an offer for your current car. Your offer is valid for 7 days, and if you save it to your Carvana account, you’ll see the benefit of your trade in as you shop.',
      'We will be there to help guide you the whole way!'
    ]
  },
  {
    q: 'How does Carvana determine the value of my vehicle?',
    a: [
      'We combine the vehicle details you provide, such as options, accident history, and mileage, along with data from service records, trusted pricing guides, and our own market analysis. We analyze hundreds of data points to create a real offer, personalized for you. You’ll receive the strongest offer we can give based on our analysis of your vehicle’s value and current auto market conditions.'
    ]
  },
  {
    q: 'What documents do I need to sell my car to Carvana?',
    a: [
      'In most cases, we ask for photos of three items: your ID, your vehicle’s title, and odometer. We tailor what we request based on considerations unique to you and your car and don’t ask for anything we don’t need. We’ll request documents unique to your situation, like if you have a loan, a lease, or multiple owners.',
      'We make it easy and secure to send documents right from your mobile phone or laptop. And we’re here to answer any questions you have along the way.'
    ]
  },
  {
    q: 'What can I expect at the appointment when I sell my car?',
    a: [
      'We offer all our customers fast, easy appointments. A friendly Carvana advocate will confirm basic information related to your vehicle and help with final paperwork to complete the sale. We’ll do a quick visual inspection, perform a simple diagnostic check, and ensure the vehicle is in safe driving condition. It only takes about 15 minutes from start to finish.',
      'Remember to bring all keys, vehicle accessories, and necessary documentation, which typically includes personal identification and vehicle title. Don’t worry, we’ll provide personalized guidance based on your situation.',
      'After we’ve confirmed your vehicle information and completed the sale documents, we’ll either hand you a check or initiate electronic payment via bank transfer (ACH).',
      'We help thousands of customers sell their cars every week, and we’re happy to make this as smooth as possible for you!'
    ]
  },
  {
    q: 'If I sell my car to Carvana, how and when will I be paid?',
    a: [
      'Carvana will pay you for your car at your pickup or drop-off appointment. We’ll either hand you a printed check or initiate payment via an ACH direct deposit into your checking or savings account (ACH transfers normally take between 2-5 business days). The appointment is fast – we simply confirm the basic details about your vehicle and complete final sale documents – ensuring you get paid as quickly as possible.'
    ]
  },
  {
    q: 'How does Carvana handle options for pickup and drop-off?',
    a: [
      'Drop-off appointments, at one of our convenient Carvana hubs or awesome vending machines, are available to everyone and are always free! Many customers choose to drive to us and get a ride home with a friend or rideshare service.',
      'Depending on your location, we also offer a pickup service. Many customers find this convenient and valuable. Given the time involved and the special equipment required, we charge for this service based on your location. The charge varies by location and can change based on demand in your area. The actual charge will be shared at the time of scheduling.',
      'Our appointments are fast and easy -  we will take a quick look at your car to confirm the vehicle details, pass you a couple of papers to sign, and then pay you on the spot!'
    ]
  },
  {
    q: 'Can I sell my car to Carvana even if I still have a loan on my vehicle?',
    a: [
      'Yes, we help lots of customers sell cars with loans. We’ll guide you through the process to gather your loan payoff information, and in some cases, we can help gather it directly from your lender. We’ll pay your loan provider directly when we buy your vehicle.',
      'If your vehicle is worth more than you owe, we’ll pay you the difference, or you can apply it toward the purchase of your next vehicle from Carvana.',
      'Until the sale of your vehicle to Carvana is completed, please continue to make your normal loan payments to avoid late payment penalties with your lender. You’ll be reimbursed for any overpayment.'
    ]
  },
  {
    q: 'Will Carvana buy my leased vehicle?',
    a: [
      'We are here to help you sell your leased car, but whether we can buy it depends on your leasing company policies. Some leasing companies allow us to pay off leases early (sometimes called a “lease buyout”), but others will not allow Carvana to pay off a customer’s lease. Include your lease information as part of your answers to the appraisal questions, and in most cases we can provide next steps with your offer.'
    ]
  },
  {
    q: 'Will the offer I receive for my car from Carvana ever change?',
    a: [
      'Your offer from Carvana is valid for 7 days. If the information you provide accurately reflects your vehicle details and condition, your offer will not change.',
      "We'll also create a new offer if you resubmit the same information at a later date. Vehicle valuations change over time and market demand is dynamic. We use information about your vehicle's condition and current auto market conditions to make the strongest offers we can.",
      'We rely on our customers to submit accurate information about their vehicle details and condition. We do a combination of automated checks, car photo reviews, and an inspection at the pick-up appointment to ensure the car matches the information submitted at appraisal. If we identify a mismatch, we’ll work with you to generate an updated offer.'
    ]
  }
];

const getMainEntity = () =>
  FAQData().map(({ q, a }) => {
    const answer = a.map(item => `<p>${item}</p>`).join('');
    return {
      '@type': 'Question',
      name: `${q}`,
      acceptedAnswer: { '@type': 'Answer', text: `${answer}` }
    };
  });

const FAQStructuredData = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: getMainEntity()
});

export const SetFAQStructuredData = () => ({ __html: JSON.stringify(FAQStructuredData()) });

type FAQSectionProps = {
  videoExperiment?: boolean;
};

const FAQSection = ({ videoExperiment }: FAQSectionProps) => {
  const getTestAttributes = useTestFunc();
  const { recordActionClicked } = useAnalyticEvent();

  return (
    <>
      <FAQSectionWrapper className={videoExperiment ? 'video-experiment' : ''}>
        <div>
          <TextContainer>
            <FAQHeader className={videoExperiment ? '' : 'capitalize'}>
              Frequently asked questions
            </FAQHeader>
            <SubText>
              Have questions about selling or trading your car? We’ve got you covered.
            </SubText>
          </TextContainer>
          <FAQButtonWrapper href="/help/sell-or-trade" className="desktop-only">
            <Button size="lg" variant="secondary" {...getTestAttributes('ViewAllFAQs')}>
              View all FAQs
            </Button>
          </FAQButtonWrapper>
        </div>
        <div>
          <FAQAccordionWrapper>
            {FAQData().map(({ q, a }) => (
              <Expander
                key={q}
                onExpanded={() => recordActionClicked('Landing Page', 'FAQ', { question: q })}
                title={q}
                expandedHeight="650px"
              >
                <>
                  {a.map(item => (
                    <p key={item.toString()}>{item}</p>
                  ))}
                </>
              </Expander>
            ))}
          </FAQAccordionWrapper>
          <FAQButtonWrapper href="/help/sell-or-trade">
            <Button
              size="lg"
              variant="secondary"
              {...getTestAttributes('ViewAllFAQs')}
              style={{ width: '100%' }}
            >
              View all FAQs
            </Button>
          </FAQButtonWrapper>
        </div>
      </FAQSectionWrapper>
    </>
  );
};

export default FAQSection;
